import React, { useCallback, useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { Package, Plus, X } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const EMPTY = {
  label: '', description: '', price_usd: '', shipping_usd: '0',
  max_quantity: '25', display_order: '99', active: true,
};

function ProductForm({ initial, onSave, onClose, saving }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="product-form-modal">
      <div className="bg-[hsl(var(--card))] rounded-2xl border border-[hsl(var(--primary)/0.15)] w-full max-w-lg mt-16 p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">{initial?.product_id ? 'Edit product' : 'New product'}</h3>
          <button onClick={onClose} className="p-1 text-[hsl(var(--primary)/0.6)]" data-testid="product-form-close"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <input className="ml-input w-full" placeholder="Product name" value={f.label} onChange={set('label')} data-testid="product-form-label" />
          <textarea className="ml-input w-full" rows={2} placeholder="Description" value={f.description || ''} onChange={set('description')} data-testid="product-form-description" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary)/0.55)]">Price (USD)</label>
              <input className="ml-input w-full" type="number" min="0" step="0.01" value={f.price_usd} onChange={set('price_usd')} data-testid="product-form-price" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary)/0.55)]">Shipping (USD, per order)</label>
              <input className="ml-input w-full" type="number" min="0" step="0.01" value={f.shipping_usd} onChange={set('shipping_usd')} data-testid="product-form-shipping" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary)/0.55)]">Max quantity</label>
              <input className="ml-input w-full" type="number" min="1" value={f.max_quantity} onChange={set('max_quantity')} data-testid="product-form-maxqty" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary)/0.55)]">Display order</label>
              <input className="ml-input w-full" type="number" min="0" value={f.display_order} onChange={set('display_order')} data-testid="product-form-order" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[hsl(var(--primary))]">
            <input type="checkbox" checked={!!f.active} onChange={set('active')} data-testid="product-form-active" />
            Active (visible & purchasable by customers)
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-[hsl(var(--primary)/0.2)] text-sm">Cancel</button>
          <button
            onClick={() => onSave({
              label: f.label.trim(),
              description: (f.description || '').trim() || null,
              price_usd: Number(f.price_usd),
              shipping_usd: Number(f.shipping_usd || 0),
              max_quantity: Number(f.max_quantity || 25),
              display_order: Number(f.display_order || 99),
              active: !!f.active,
            })}
            disabled={saving || !f.label.trim() || f.price_usd === '' || Number(f.price_usd) < 0}
            className="px-5 py-2 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--accent))] text-sm font-medium disabled:opacity-50"
            data-testid="product-form-save"
          >
            {saving ? 'Saving…' : 'Save product'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get('/admin/products')
      .then(({ data }) => setProducts(data.products || []))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing?.product_id) {
        await api.patch(`/admin/products/${editing.product_id}`, payload);
        toast.success('Product updated. New price applies to all future checkouts immediately.');
      } else {
        await api.post('/admin/products', payload);
        toast.success('Product created.');
      }
      setEditing(null); setCreating(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/products/${p.product_id}`, { active: !p.active });
      toast.success(p.active ? 'Product deactivated — hidden from customers.' : 'Product activated.');
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
            <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-products-title">Product catalog</h1>
            <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-2xl">
              Physical products, pricing and shipping. Changes apply to all new checkouts
              instantly — no code changes needed.
            </p>
          </div>
          <button onClick={() => setCreating(true)} className="ml-btn-primary flex items-center gap-1.5 text-xs px-4 py-2.5" data-testid="admin-products-new-btn">
            <Plus size={14} /> New product
          </button>
        </header>

        <div className="space-y-3">
          {products.map((p) => (
            <div key={p.product_id} className={`rounded-2xl border px-6 py-4 flex flex-wrap items-center gap-4 ${p.active ? 'border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))]' : 'border-[hsl(var(--primary)/0.1)] bg-[hsl(var(--primary)/0.03)] opacity-70'}`}
                 data-testid={`admin-product-${p.product_id}`}>
              <Package size={20} className="text-[hsl(var(--accent))] shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-serif text-lg text-[hsl(var(--primary))]">
                  {p.label}
                  {!p.active && <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 font-semibold font-sans">Inactive</span>}
                </div>
                <div className="text-xs text-[hsl(var(--primary)/0.6)]">{p.description || '—'}</div>
                <div className="text-[10px] text-[hsl(var(--primary)/0.45)] mt-0.5 font-mono">{p.product_id} · order {p.display_order} · max qty {p.max_quantity}</div>
              </div>
              <div className="text-right">
                <div className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid={`admin-product-price-${p.product_id}`}>{money(p.price_usd)}</div>
                {Number(p.shipping_usd) > 0 && <div className="text-[10px] text-[hsl(var(--primary)/0.55)]">+ {money(p.shipping_usd)} shipping</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(p)} className="ml-btn-secondary text-xs px-3 py-1.5" data-testid={`admin-product-edit-${p.product_id}`}>Edit</button>
                <button onClick={() => toggleActive(p)} className="ml-btn-secondary text-xs px-3 py-1.5" data-testid={`admin-product-toggle-${p.product_id}`}>
                  {p.active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {(editing || creating) && (
          <ProductForm
            initial={editing || {}}
            onSave={save}
            onClose={() => { setEditing(null); setCreating(false); }}
            saving={saving}
          />
        )}
      </AdminLayout>
    </div>
  );
}
